import { GraphQLBilletsRequester, type MultipleContractDto } from '@cobliteam/api-lib';
import type { Billet } from '@cobliteam/api-lib/src/__generated__/graphql';
import { usePermissions } from '@cobliteam/permissions';
import { calculateOverdueDays } from '@cobliteam/utils/date';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

const QUERY_KEY = 'BILLETS';
const PERMISSION_WRITE_HUBSPOT_FINANCE = 'write:hubspot-finance';

type GetBilletsParams = {
  companyId: string;
  options?: Partial<UseQueryOptions<Billet[]>>;
};

let requesterInstance: GraphQLBilletsRequester | null = null;
const getRequester = () => {
  if (!requesterInstance) {
    requesterInstance = new GraphQLBilletsRequester();
  }
  return requesterInstance;
};

const useBillets = (params: GetBilletsParams) => {
  const { isAccessAllowed } = usePermissions();
  const hasFinancePermission = isAccessAllowed(PERMISSION_WRITE_HUBSPOT_FINANCE);

  return useQuery<Billet[]>({
    ...params.options,
    queryKey: [QUERY_KEY, params.companyId, hasFinancePermission],
    queryFn: () => getRequester().getBillets(params.companyId),
    enabled: !!params.companyId && hasFinancePermission && (params.options?.enabled ?? true),
  });
};

const useOverdueBillets = (multipleContracts?: MultipleContractDto) => {
  const { isAccessAllowed } = usePermissions();
  const hasFinancePermission = isAccessAllowed(PERMISSION_WRITE_HUBSPOT_FINANCE);

  const companyIds = multipleContracts?.data?.map((contract) => contract.companyId).filter(Boolean);
  const hasContracts = !!companyIds && companyIds.length > 0;

  const { data, isLoading, isFetching, isError } = useQuery<Billet[]>({
    queryKey: [QUERY_KEY, 'byCompanies', companyIds],
    queryFn: () => getRequester().getBilletsByCompanies(companyIds!),
    enabled: hasContracts && hasFinancePermission,
  });

  const overdueBillets = (data ?? []).filter((billet) => {
    if (!['03', '07'].includes(billet.status || '')) return false;
    if (!billet.dataVencimento) return false;
    return calculateOverdueDays(billet.dataVencimento) > 0;
  });

  return { data: overdueBillets, isLoading, isFetching, isError };
};

export { QUERY_KEY, useBillets, useOverdueBillets };
export default useBillets;
